/**
 * Pure Classic tennis score logic - shared by phone app, web and watch.
 * @param {object} state - { sets, games, points, isTieBreak, setScores }
 * @param {number} scoringTeam - 1 or 2
 * @param {number} bestOfSets - 1 or 3
 * @param {Array} currentHistory
 * @param {boolean} goldenPoint
 * @returns {object|null} - scoring step outcome including newScore, newHistory, matchWinner
 */
export function computePointResult(
  state,
  scoringTeam,
  bestOfSets = 3,
  currentHistory = [],
  goldenPoint = false,
  currentServer = null
) {
  const { sets = [0, 0], games = [0, 0], points = [0, 0], isTieBreak = false, setScores = [] } = state || {};
  const p1 = scoringTeam === 1 ? points[0] + 1 : points[0];
  const p2 = scoringTeam === 2 ? points[1] + 1 : points[1];

  const isPointGame = (a, b) => {
    // In tiebreaks, golden point never applies; must reach at least 7 with 2-point lead
    if (isTieBreak) return (a >= 7 || b >= 7) && Math.abs(a - b) >= 2;
    if (goldenPoint && (a >= 4 || b >= 4) && Math.min(a, b) >= 3) return true;
    return (a >= 4 || b >= 4) && Math.abs(a - b) >= 2;
  };

  const prePointEntry = {
    points: [points[0], points[1]],
    games: [...games],
    sets: [...sets],
    isTieBreak,
    server: currentServer || state?.server || null,
  };

  if (!isPointGame(p1, p2)) {
    const newScore = {
      sets: [...sets],
      games: [...games],
      points: [p1, p2],
      isTieBreak,
      setScores: [...setScores],
    };
    const newHistory = [...currentHistory, prePointEntry];
    return {
      newScore,
      newHistory,
      matchWinner: null,
      setWinner: null,
      gameWinner: null,
      setJustWon: false,
      gameJustWon: false,
      p1,
      p2,
      g1: games[0],
      g2: games[1],
      stillTieBreak: isTieBreak,
    };
  }

  const gameWinner = p1 > p2 ? 1 : 2;
  const newGames = [games[0] + (gameWinner === 1 ? 1 : 0), games[1] + (gameWinner === 2 ? 1 : 0)];
  const setsToWin = Math.ceil(bestOfSets / 2);
  const g1 = newGames[0];
  const g2 = newGames[1];
  let newSets = [...sets];
  let stillTieBreak = false;

  if (isTieBreak) {
    newSets = [sets[0] + (gameWinner === 1 ? 1 : 0), sets[1] + (gameWinner === 2 ? 1 : 0)];
  } else if ((g1 >= 6 || g2 >= 6) && Math.abs(g1 - g2) >= 2) {
    newSets = [sets[0] + (gameWinner === 1 ? 1 : 0), sets[1] + (gameWinner === 2 ? 1 : 0)];
  } else if (g1 === 6 && g2 === 6) {
    stillTieBreak = true;
  }

  const setJustWon = newSets[0] !== sets[0] || newSets[1] !== sets[1];
  const updatedSetScores = setJustWon ? [...setScores, { team1: g1, team2: g2 }] : [...setScores];
  const setWinner = setJustWon ? (newSets[0] > sets[0] ? 1 : 2) : null;
  const matchWinner = newSets[0] >= setsToWin ? 1 : newSets[1] >= setsToWin ? 2 : null;

  if (matchWinner) {
    const winningScore = {
      sets: newSets,
      games: [0, 0],
      points: [0, 0],
      isTieBreak: false,
      setScores: updatedSetScores,
    };
    const winningHistory = [
      ...currentHistory,
      {
        ...prePointEntry,
        gameRecorded: true,
        setWon: true,
        matchWon: true,
      },
    ];
    return {
      newScore: winningScore,
      newHistory: winningHistory,
      matchWinner,
      setWinner,
      gameWinner,
      setJustWon: true,
      gameJustWon: true,
      p1,
      p2,
      g1,
      g2,
      stillTieBreak: false,
    };
  }

  const newScore = {
    sets: stillTieBreak ? sets : newSets,
    games: stillTieBreak ? newGames : (setJustWon ? [0, 0] : newGames),
    points: [0, 0],
    isTieBreak: stillTieBreak,
    setScores: updatedSetScores,
  };
  const newHistory = [
    ...currentHistory,
    {
      ...prePointEntry,
      gameRecorded: true,
      setWon: setJustWon,
    },
  ];

  return {
    newScore,
    newHistory,
    matchWinner: null,
    setWinner,
    gameWinner,
    setJustWon,
    gameJustWon: true,
    p1,
    p2,
    g1,
    g2,
    stillTieBreak,
  };
}

export function computeUndoResult(state, history) {
  if (!history || history.length === 0) return null;
  const prev = history[history.length - 1];
  let revertedSetScores = state?.setScores ? [...state.setScores] : [];
  const computedPrevSets = prev.sets || [
    revertedSetScores.filter(s => (s.team1 ?? s[0] ?? 0) > (s.team2 ?? s[1] ?? 0)).length,
    revertedSetScores.filter(s => (s.team2 ?? s[1] ?? 0) > (s.team1 ?? s[0] ?? 0)).length,
  ];
  const prevCompletedSets = (computedPrevSets[0] || 0) + (computedPrevSets[1] || 0);
  if (revertedSetScores.length > prevCompletedSets) {
    revertedSetScores = revertedSetScores.slice(0, prevCompletedSets);
  }

  const p1 = prev.points?.[0] || 0;
  const p2 = prev.points?.[1] || 0;
  const isGameOverPoints = prev.isTieBreak
    ? ((p1 >= 7 || p2 >= 7) && Math.abs(p1 - p2) >= 2)
    : ((p1 >= 4 || p2 >= 4) && Math.abs(p1 - p2) >= 2);
  const revertedPoints = isGameOverPoints ? [0, 0] : (prev.points || [0, 0]);

  return {
    newScore: {
      sets: computedPrevSets,
      games: prev.games ? [...prev.games] : [0, 0],
      points: revertedPoints,
      isTieBreak: prev.isTieBreak !== undefined ? Boolean(prev.isTieBreak) : Boolean(state?.isTieBreak),
      setScores: revertedSetScores,
    },
    newHistory: history.slice(0, -1),
    undoneEntry: prev,
    previousServer: prev.server || null,
    gameUndone: Boolean(prev.gameRecorded || prev.setWon || prev.matchWon),
  };
}

export function pointsToDisplay(p1, p2, isTieBreak = false) {
  if (isTieBreak) return [String(p1), String(p2)];
  const word = (p) => (p === 0 ? '0' : p === 1 ? '15' : p === 2 ? '30' : p === 3 ? '40' : '0');
  if (p1 >= 4 && p1 - p2 >= 2) return ['Game', ''];
  if (p2 >= 4 && p2 - p1 >= 2) return ['', 'Game'];
  if (p1 >= 4 && p2 >= 3 && p1 > p2) return ['A', '40'];
  if (p2 >= 4 && p1 >= 3 && p2 > p1) return ['40', 'A'];
  if (p1 >= 3 && p2 >= 3 && p1 === p2) return ['40', '40'];
  return [word(p1), word(p2)];
}

export function emptyClassic() {
  return { sets: [0, 0], games: [0, 0], points: [0, 0], isTieBreak: false, setScores: [] };
}
